import React, { useState, useEffect } from 'react';
import { useLocation, useLoaderData } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Clock, Euro, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../components/ui/Button';
import { getServiceCategories, getServicesByCategorySlug } from '../services/api';
import { withRetry } from '../utils/withRetry';
import type { ServiceCategory, BackendService } from '../types/api';

// Import des images
import bainEnveloppeImg from '../assets/bain_enveloppe.png';
import salleMassageImg from '../assets/salle_massage.jpg';
import mainCoeurImg from '../assets/maincoeur.png';
import kidSpaImg from '../assets/kid_spa.png';
import massageDuoImg from '../assets/massage_duo.png';
import massageSoloImg from '../assets/massage_solo.png';
import spaBebeDuoImg from '../assets/spa_bebe_duo.png';
import spaBebeImg from '../assets/spa_bebe.png';
import massagePostNatalImg from '../assets/massage_postnatal.png';

export interface ServicesLoaderData {
  categories: ServiceCategory[];
  servicesByCategory: Record<string, BackendService[]>;
}

/**
 * Loader React Router : charge catégories + soins AU BUILD (SSG) afin que le
 * contenu (noms, descriptions, prix) soit présent dans le HTML — meilleur SEO.
 * Servi depuis le manifest au chargement initial, rappelé en navigation client.
 * Tolérant : en cas d'échec, renvoie des listes vides (le build ne casse pas).
 */
export async function servicesLoader(): Promise<ServicesLoaderData> {
  // Gracieux des deux côtés (liste vide plutôt que page cassée). La garantie
  // « pas de /services vide en prod » est assurée par l'assertion post-build
  // (scripts/assert-ssg-content.mjs), car vite-react-ssg avale les erreurs de
  // loader au build (un throw ne ferait pas échouer le build).
  try {
    const categories = await withRetry(() => getServiceCategories());
    const servicesByCategory: Record<string, BackendService[]> = {};
    for (const category of categories) {
      try {
        servicesByCategory[category.slug] = await withRetry(() => getServicesByCategorySlug(category.slug));
      } catch {
        servicesByCategory[category.slug] = [];
      }
    }
    return { categories, servicesByCategory };
  } catch {
    return { categories: [], servicesByCategory: {} };
  }
}

const Services: React.FC = () => {
  const loaderData = useLoaderData() as ServicesLoaderData;
  // Les données du loader sont figées au build (SSG) : elles servent au HTML
  // pré-rendu (SEO) et au premier affichage, mais peuvent être périmées si un
  // service a été modifié dans le backoffice depuis le dernier déploiement.
  // On revalide donc côté client après l'hydratation et on remplace par les
  // données fraîches de l'API dès qu'elles arrivent (silencieux en cas d'échec).
  const [freshData, setFreshData] = useState<ServicesLoaderData | null>(null);
  const [expandedServices, setExpandedServices] = useState<Set<number>>(new Set());
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    servicesLoader().then((data) => {
      if (!cancelled && data.categories.length > 0) {
        setFreshData(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { categories, servicesByCategory } = freshData ?? loaderData;

  useEffect(() => {
    if (!location.hash) return;

    const anchor = location.hash.slice(1);
    if (!anchor) return;

    requestAnimationFrame(() => {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [location.hash, servicesByCategory]);

  const categoryTitles: Record<string, string> = {
    'pour-moman': 'Pour Moman',
    'pour-bebe': 'Pour Bébé',
    'pour-bambin': 'Pour Bambin',
  };

  const toggleServiceExpansion = (serviceId: number) => {
    setExpandedServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  // Mapping des images et styles de fond par nom de service
  const getServiceImage = (serviceName: string): { image: string; bgGradient: string } => {
    const lowerName = serviceName.toLowerCase();
    
    if (lowerName.includes('bain enveloppé') || lowerName.includes('bain enveloppe')) {
      return { 
        image: bainEnveloppeImg, 
        bgGradient: 'bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50' 
      };
    }
    if (lowerName.includes('massage prénatal duo') || lowerName.includes('massage prenatal duo')) {
      return { 
        image: massageDuoImg, 
        bgGradient: 'bg-gradient-to-br from-orange-50 via-orange-100 to-amber-50' 
      };
    }
    if (lowerName.includes('massage prénatal') || lowerName.includes('massage prenatal')) {
      return {
        image: massageSoloImg,
        bgGradient: 'bg-gradient-to-br from-orange-50 via-orange-100 to-amber-50'
      };
    }
    if (lowerName.includes('post-natal') || lowerName.includes('postnatal') || lowerName.includes('post natal')) {
      return {
        image: massagePostNatalImg,
        bgGradient: 'bg-gradient-to-br from-orange-50 via-orange-100 to-amber-50'
      };
    }
    if (lowerName.includes('spa bébé duo') || lowerName.includes('spa bebe duo')) {
      return { 
        image: spaBebeDuoImg, 
        bgGradient: 'bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50' 
      };
    }
    if (lowerName.includes('spa bébé') || lowerName.includes('spa bebe')) {
      return { 
        image: spaBebeImg, 
        bgGradient: 'bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50' 
      };
    }
    if (lowerName.includes('kid spa') || lowerName.includes('kids spa')) {
      return { 
        image: kidSpaImg, 
        bgGradient: 'bg-gradient-to-br from-blue-50 via-blue-100 to-cyan-50' 
      };
    }
    if (lowerName.includes('massage bébé') || lowerName.includes('massage bebe')) {
      return { 
        image: mainCoeurImg, 
        bgGradient: 'bg-gradient-to-br from-amber-50 via-orange-100 to-amber-50' 
      };
    }
    
    // Image par défaut
    return { 
      image: salleMassageImg, 
      bgGradient: 'bg-gradient-to-br from-accent-sand via-accent-rose/20 to-accent-sand' 
    };
  };

  const servicesJsonLd = React.useMemo(() => {
    const allServices = Object.values(servicesByCategory).flat();
    if (allServices.length === 0) return undefined;
    return allServices.map((s) => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: s.name,
      description: s.description || s.detailed_description || '',
      provider: {
        '@type': 'DaySpa',
        '@id': 'https://douxcemoman.fr/#business',
        name: 'Doux ce Moman',
        url: 'https://douxcemoman.fr',
      },
      areaServed: { '@type': 'Place', name: 'Besançon, Geneuille, Doubs, France' },
      ...(s.price ? {
        offers: {
          '@type': 'Offer',
          price: (s.price / 100).toFixed(2),
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: 'https://douxcemoman.fr/reservation',
        },
      } : {}),
      ...(s.duration_minutes ? { duration: `PT${s.duration_minutes}M` } : {}),
    }));
  }, [servicesByCategory]);

  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead
        title="Nos Soins Périnatal & Spa Bébé"
        description="Découvrez nos soins : massage prénatal, spa bébé, bain enveloppé et ateliers bien-être à Geneuille près de Besançon (Doubs)."
        path="/services"
        jsonLd={servicesJsonLd}
      />
      <div className="container mx-auto px-4">
        <header className="text-center mb-16">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
            Nos Services
          </h1>
          <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
            Découvrez notre gamme complète de services dédiés au bien-être des futures mamans et de leurs bébés.
          </p>
        </header>

        {categories.map((category) => {
          const categoryServices = servicesByCategory[category.slug] || [];
          const displayTitle = categoryTitles[category.slug] || category.name;

          if (categoryServices.length === 0) return null;

          return (
            <section key={category.id} id={category.slug} className="mb-20 scroll-mt-24 md:scroll-mt-28">
              <div className="text-center mb-12">
                <h2 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4">
                  {displayTitle}
                </h2>
                <p className="font-nunito text-neutral-body max-w-xl mx-auto text-base md:text-lg leading-relaxed whitespace-pre-line">
                  {category.description}
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-8">
                {categoryServices.map((service) => {
                  const { image, bgGradient } = getServiceImage(service.name);
                  
                  return (
                  <div
                    key={service.id}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 w-full max-w-sm group"
                  >
                    <div className={`relative h-48 ${bgGradient} flex items-center justify-center overflow-hidden transition-all duration-500`}>
                      {/* Effet de brillance diagonal animé au hover */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-white/20 to-white/5 opacity-80"></div>
                      
                      {/* Cercles décoratifs multiples avec animation */}
                      <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-white/25 blur-3xl group-hover:scale-125 transition-transform duration-500"></div>
                      <div className="absolute bottom-6 left-6 w-20 h-20 rounded-full bg-white/20 blur-2xl group-hover:scale-125 transition-transform duration-500"></div>
                      <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full bg-white/15 blur-xl group-hover:scale-110 transition-transform duration-500"></div>
                      
                      {/* Effet de halo central */}
                      <div className="absolute inset-0 bg-radial-gradient from-white/10 via-transparent to-transparent"></div>
                      
                      {/* Bordure lumineuse subtile */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/5"></div>
                      
                      <img
                        src={image}
                        alt={service.name}
                        loading="lazy"
                        className="relative z-10 h-[120%] w-auto object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.2)] group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-playfair text-2xl font-bold text-neutral-ink mb-3">
                        {service.name}
                      </h3>

                      {service.target_audience && (
                        <div className="mb-3">
                          <span className="inline-flex items-center gap-1 bg-accent-sand/30 text-brand px-3 py-1.5 rounded-full text-xs font-semibold border border-accent-sand">
                            <span className="text-sm">👶</span>
                            {service.target_audience}
                          </span>
                        </div>
                      )}

                      <div className="mb-4">
                        <div
                          className={`relative overflow-hidden transition-all duration-500 ease-in-out ${
                            expandedServices.has(service.id) ? 'max-h-[2000px]' : 'max-h-[120px]'
                          } cursor-pointer`}
                          role="button"
                          tabIndex={0}
                          aria-expanded={expandedServices.has(service.id)}
                          onClick={() => toggleServiceExpansion(service.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleServiceExpansion(service.id);
                            }
                          }}
                        >
                          <p className="font-nunito text-neutral-body text-base leading-relaxed whitespace-pre-line">
                            {service.detailed_description || service.description}
                          </p>
                          {/* Dégradé de fondu en bas quand le texte est réduit */}
                          {!expandedServices.has(service.id) && (service.detailed_description || service.description || '').length > 200 && (
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>
                          )}
                        </div>
                        {(service.detailed_description || service.description || '').length > 200 && (
                          <div className="flex justify-center">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleServiceExpansion(service.id);
                              }}
                              className="group mt-3 inline-flex items-center gap-1 text-brand text-sm font-semibold hover:gap-2 transition-all duration-300 focus:outline-none relative"
                            >
                              <span className="relative">
                                {expandedServices.has(service.id) ? 'Réduire' : 'Lire la suite'}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand group-hover:w-full transition-all duration-300"></span>
                              </span>
                              {expandedServices.has(service.id) ? (
                                <ChevronUp className="w-3 h-3 group-hover:animate-bounce" />
                              ) : (
                                <ChevronDown className="w-3 h-3 group-hover:animate-bounce" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>

                      {service.benefits && (
                        <div className="mb-4">
                          <h4 className="font-nunito font-semibold text-neutral-ink text-base mb-2">Bienfaits :</h4>
                          <ul className="text-sm text-neutral-body space-y-1.5">
                            {service.benefits.split(', ').map((benefit, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-brand mr-2">•</span>
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="text-brand" size={16} />
                          <span className="font-nunito text-neutral-body text-base">{service.duration_minutes} min</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Euro className="text-brand" size={16} />
                          <span className="font-nunito text-neutral-body text-base">
                            {service.price ? `${(service.price / 100).toFixed(0)}€` : 'Sur devis'}
                          </span>
                        </div>
                      </div>

                      {service.is_bookable ? (
                        <Button
                          variant="outline"
                          size="sm"
                          to={`/reservation?service=${service.id}&category=${category.slug}`}
                          className="w-full group relative overflow-hidden border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 active:shadow-sm !bg-transparent hover:!bg-transparent"
                        >
                          {/* Effet de liquide qui remplit */}
                          <span className="absolute inset-0 bg-brand opacity-0 translate-y-full group-hover:opacity-100 group-hover:animate-liquid-fill transition-opacity duration-300"></span>

                          {/* Vague ondulante qui monte puis continue */}
                          <span className="absolute inset-x-0 top-0 h-16 bg-brand-dark opacity-0 translate-y-full group-hover:opacity-40 group-hover:animate-wave-fill group-hover:[animation-delay:0.6s] transition-opacity duration-300"></span>

                          {/* Vague continue après remplissage */}
                          <span className="absolute inset-x-0 top-0 h-20 bg-brand-dark/30 opacity-0 group-hover:opacity-100 group-hover:animate-wave-continuous [animation-delay:1.4s] transition-opacity duration-700"></span>

                          <span className="relative z-10 text-brand group-hover:text-white transition-colors duration-500 flex items-center justify-center gap-2">
                            <span>Réserver</span>
                            <svg className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center gap-1 bg-accent-sand/40 text-brand-dark px-3 py-1.5 rounded-full text-xs font-semibold border border-accent-sand">
                              Bientôt disponible — formation en cours
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            to={`/bons-cadeaux?service=${service.id}`}
                            className="w-full border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 !bg-transparent hover:!bg-transparent"
                          >
                            <span className="relative z-10 font-semibold text-brand">
                              Offrir en bon cadeau
                            </span>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <section className="bg-neutral-offwhite rounded-2xl p-8 md:p-12 mt-20 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-neutral-ink mb-4">
            Vous avez des questions ?
          </h2>
          <p className="font-nunito text-neutral-body max-w-xl mx-auto text-base md:text-lg leading-relaxed mb-8">
            Consultez notre page FAQ pour trouver des réponses détaillées sur nos services, les contre-indications et les informations pratiques.
          </p>
          <Button to="/faq" variant="primary" size="lg">
            Consulter la FAQ
          </Button>
        </section>
      </div>
    </article>
  );
};

export default Services;